import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Image as ImageIcon, Loader2, Download, Copy, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { safeLS } from "@/lib/safeStorage";

const SECTIONS = [
  { key: "resumen", label: "Resumen (KPIs, Qué atender hoy, alertas)" },
  { key: "cobranzas", label: "Cobranzas del mes" },
  { key: "cuentas", label: "Cuentas por recibir" },
  { key: "sinlinea", label: "Clientes sin línea de crédito vigente" },
  { key: "excedidas", label: "Líneas excedidas y casos judiciales" },
  { key: "pagares", label: "Pagarés pendientes de firma" },
  { key: "indice", label: "Índice de gestión por vendedor" },
];

export default function DescargarWhatsApp({ open, onOpenChange, mes, vendedor }) {
  const [formato, setFormato] = useState(() => safeLS.getItem("wa_formato") || "pdf");
  const [sel, setSel] = useState(() => {
    try { const s = JSON.parse(safeLS.getItem("wa_secciones") || "null"); if (Array.isArray(s) && s.length) return s; } catch (e) { /* */ }
    return SECTIONS.map((s) => s.key);
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { safeLS.setItem("wa_formato", formato); }, [formato]);
  useEffect(() => { safeLS.setItem("wa_secciones", JSON.stringify(sel)); }, [sel]);
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;
  const vsafe = (vendedor && vendedor !== "__all__") ? vendedor.replace(/[^A-Za-z0-9]+/g, "_") : "General";

  const toggle = (k) => setSel((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  const allOn = sel.length === SECTIONS.length;

  const fetchBlob = async () => {
    const path = formato === "pdf" ? "/cartera-wa.pdf" : "/cartera-wa.png";
    const r = await api.get(path, { params: { mes, vendedor, sections: sel.join(","), _t: Date.now() }, responseType: "blob" });
    return r.data;
  };

  const run = async (mode) => {
    if (!sel.length) return toast.error("Elegí al menos una sección.");
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const isZip = formato === "img" && sel.length > 1;
      const ext = formato === "pdf" ? "pdf" : (isZip ? "zip" : "png");
      const file = new File([blob], `Cartera_${vsafe}_${mes}.${ext}`, { type: blob.type });
      if (mode === "share" && canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: "Informe de cartera" }); setBusy(false); return; } catch (e) { /* fallback */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = file.name; a.click();
      URL.revokeObjectURL(url);
      toast.success("Descarga lista. En WhatsApp enviala como Documento para máxima calidad.", { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "No tenés acceso a ese vendedor." : "No se pudo generar la descarga.");
    } finally { setBusy(false); }
  };

  const copiar = async () => {
    try {
      const r = await api.get("/cartera-wa/texto", { params: { mes, vendedor } });
      await navigator.clipboard.writeText(r.data.texto);
      toast.success("Texto copiado para WhatsApp.");
    } catch { toast.error("No se pudo copiar el texto."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="wa-download-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-900">
            <Share2 className="h-5 w-5" /> Descargar para WhatsApp
          </DialogTitle>
          <DialogDescription>Formato vertical optimizado para celular.</DialogDescription>
        </DialogHeader>

        <div className="text-xs font-semibold text-slate-500 uppercase mt-1">Formato</div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="wa-fmt-pdf" onClick={() => setFormato("pdf")} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${formato === "pdf" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200"}`}>
            <FileText className="h-4 w-4" /> PDF vertical (A4)
          </button>
          <button data-testid="wa-fmt-img" onClick={() => setFormato("img")} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${formato === "img" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200"}`}>
            <ImageIcon className="h-4 w-4" /> Imagen (1080×1350)
          </button>
        </div>

        <div className="flex items-center justify-between mt-2">
          <span className="text-xs font-semibold text-slate-500 uppercase">Secciones</span>
          <button data-testid="wa-toggle-all" onClick={() => setSel(allOn ? [] : SECTIONS.map((s) => s.key))} className="text-xs text-emerald-700 font-medium">
            {allOn ? "Ninguna" : "Marcar todas"}
          </button>
        </div>
        <div className="max-h-44 overflow-auto rounded-lg border border-slate-100 p-1">
          {SECTIONS.map((s) => (
            <label key={s.key} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-emerald-50 cursor-pointer text-sm">
              <Checkbox checked={sel.includes(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`wa-sec-${s.key}`} />
              {s.label}
            </label>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button data-testid="wa-download-btn" onClick={() => run("download")} disabled={busy} className="flex-1 bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Descargar
          </Button>
          {canShare && (
            <Button data-testid="wa-share-btn" variant="outline" onClick={() => run("share")} disabled={busy} className="border-emerald-200 text-emerald-800">
              <Share2 className="h-4 w-4 mr-1.5" /> Compartir
            </Button>
          )}
          <Button data-testid="wa-copy-btn" variant="outline" onClick={copiar} className="border-slate-200">
            <Copy className="h-4 w-4 mr-1.5" /> Copiar texto
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
